// import './Overrides.module.css';

import {Box} from '@dropbox/dig-foundations';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {normalizedRoute, useTTVCRouteChanges} from 'analytics/ttvc';
import {isPrivatePageAtom, pulseTokenAtom, type PulseUser, pulseUserAtom} from 'atoms/auth';
import {employeeAtom, loggedInEmployeeAtom} from 'atoms/employee';
import {
  buildGrowthbookExperimentValuesFromSdk,
  buildGrowthbookFeatureFlagsFromSdk,
  defaultGrowthbookExperimentValues,
  defaultGrowthbookFeatureFlags,
  growthbookCacheAtom,
  growthbookReadyAtom,
  isMobileAtom,
  setGrowthbookExperimentsAtom,
} from 'atoms/layout';
import {newsPostIdAtom} from 'atoms/news';
import {filterAtom, showSearchAtom} from 'atoms/search';
import {ApiError, Employee, EmployeeService} from 'client';
import {AiSearchDrawer} from 'components/ai/AiSearchDrawer';
import {LeftNav} from 'components/layout/LeftNav';
import {Navbar} from 'components/Navbar';
import {useNotificationArchiver} from 'components/notifications/utils/useNotificationArchiver';
import {GlobalSnackbar} from 'components/shared/GlobalSnackbar';
import {ROUTE_PATHS} from 'constant';
import {growthbook} from 'helpers/growthbook';
import {getNotificationLinkAnalytics} from 'helpers/notificationAnalytics';
import {isCompanyEmail} from 'helpers/utils';
import {
  fetchWorkstreamDraftPolicy,
  workstreamDraftPolicyQueryKey,
} from 'helpers/workstreamDraftPolicy';
import {useMobile} from 'hooks/isMobile';
import {useGrowthbookQueryOverrides} from 'hooks/useGrowthbookQueryOverrides';
import {useJWTToken} from 'hooks/useJWTToken';
import {usePresenceTracking} from 'hooks/usePresenceTracking';
import {useRecordVisit} from 'hooks/useRecordVisit';
import {useAtom, useAtomValue, useSetAtom} from 'jotai';
import {useEffect, useLayoutEffect, useRef} from 'react';
import {
  Outlet,
  ScrollRestoration,
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router';
import {getSprig} from 'survey/sprig';
import {getEmployeeService, getService} from 'utilities';
import {PlanningDocNavPlaceholder} from 'views/planning/PlanningDocNav';
import {ScrollToHash} from 'views/ScrollToHash';

import {QueryClientWrapper} from './QueryClientWrapper';
import {SearchOverlay} from './SearchOverlay';

function getUserType(user: PulseUser | undefined): string {
  if (!user) return 'standard';
  if (user.is_superuser) return 'superuser';
  if (user.is_useradmin) return 'useradmin';
  if (user.is_contenteditor) return 'contenteditor';
  if (user.is_team_admin) return 'team_admin';
  return 'standard';
}

/**
 * This component wraps logged in pages.
 *
 * In addition to enabling react-query, it also adds the following components:
 *  - NavBar
 *  - LeftNav
 *  - GlobalSearch
 */
export const LoggedInPage = () => {
  const [searchParams] = useSearchParams();
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const setIsAuthRequired = useSetAtom(isPrivatePageAtom);
  const setNewsPost = useSetAtom(newsPostIdAtom);
  const location = useLocation();

  const isLoggedIn = useAtomValue(pulseTokenAtom);
  const isMobile = useMobile();
  const isEditorDoc = !!useMatch(ROUTE_PATHS.PLANNING_DOC);
  const isSearchApp = !!useMatch(ROUTE_PATHS.SEARCH_APP);
  const [, setIsMobileState] = useAtom(isMobileAtom);
  const setShowSearch = useSetAtom(showSearchAtom);
  const setFilter = useSetAtom(filterAtom);
  const navigate = useNavigate();

  useTTVCRouteChanges();

  useEffect(() => {
    setIsAuthRequired(true);
  }, [setIsAuthRequired]);

  useEffect(() => {
    setIsMobileState(isMobile);
  }, [isMobile, setIsMobileState]);

  useEffect(() => {
    if (searchParams.get('s')) {
      setShowSearch('queryparam');
    }
  }, [navigate, searchParams, setShowSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target as Node)) {
        setShowSearch(false);
        setFilter('all');
        if (searchParams.get('s')) {
          navigate(window.location.pathname, {replace: true});
        }
      }
    };

    const handleEscapePress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowSearch(false);
        if (searchParams.get('s')) {
          navigate(window.location.pathname, {replace: true});
        }
      }
    };

    const handleSearchShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setFilter('all');
        setShowSearch('keyboard');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapePress);
    document.addEventListener('keydown', handleSearchShortcut);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapePress);
      document.removeEventListener('keydown', handleSearchShortcut);
    };
  }, [navigate, overlayRef, searchParams, setFilter, setShowSearch]);

  useEffect(() => {
    if (!location.pathname.startsWith('/news')) {
      setNewsPost(undefined);
    }
  }, [location.pathname, setNewsPost]);

  if (!isLoggedIn) {
    return null;
  }

  // The installable search app is the box alone: no nav, and no overlay
  // since the page itself is the search. Ask still opens in its drawer.
  if (isSearchApp) {
    return (
      <QueryClientWrapper>
        <ProfileLoader>
          <Outlet />
          <GlobalSnackbar bottomOffsetPx={16} />
          <AiSearchDrawer />
        </ProfileLoader>
      </QueryClientWrapper>
    );
  }

  return (
    <QueryClientWrapper>
      <ProfileLoader>
        <SearchOverlay overlayRef={overlayRef} />

        {/* {location.pathname === '/' && (
          <Box className={isDarkMode ? styles.homeDark : styles.home} />
        )} */}

        {isEditorDoc ? <PlanningDocNavPlaceholder /> : <Navbar />}
        {!isEditorDoc && <LeftNav />}
        <Box
          id="main"
          style={{
            marginTop: 52,
            minHeight: 'calc(100vh - 52px)',
            marginLeft: isEditorDoc ? 0 : 56,
            boxSizing: 'border-box',
          }}
        >
          <Outlet />
        </Box>
        <GlobalSnackbar bottomOffsetPx={isEditorDoc ? 60 : 16} />
        <AiSearchDrawer />
        <ScrollRestoration />
        <ResetScrollOnReload />
        <ScrollToHash />
      </ProfileLoader>
    </QueryClientWrapper>
  );
};

const ResetScrollOnReload = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    if (location.hash || typeof window === 'undefined') {
      return;
    }

    const navigationEntry = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;

    if (navigationEntry?.type === 'reload') {
      window.scrollTo(0, 0);
    }
  }, [location.hash]);

  return null;
};

const getRange = () => {
  const width = window.innerWidth;

  if (width <= 600) {
    return '<=600';
  }
  if (width <= 1024) {
    return '>600 and <=1024';
  }
  if (width <= 1280) {
    return '>1024 and <=1280';
  }
  if (width <= 1440) {
    return '>1280 and <=1440';
  }
  if (width <= 1600) {
    return '>1440 and <=1600';
  }
  if (width <= 1920) {
    return '>1600 and <=1920';
  }
  return '>1920';
};

const getBrowserType = () => {
  const userAgent = navigator.userAgent;
  let browserType;

  if (userAgent.indexOf('Firefox') > -1) {
    browserType = 'Firefox';
  } else if (userAgent.indexOf('Chrome') > -1) {
    browserType = 'Chrome';
  } else if (userAgent.indexOf('Safari') > -1) {
    browserType = 'Safari';
  } else if (userAgent.indexOf('Opera') > -1 || userAgent.indexOf('OPR') > -1) {
    browserType = 'Opera';
  } else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident') > -1) {
    browserType = 'Internet Explorer';
  } else {
    browserType = 'Unknown';
  }

  return browserType;
};

const decodePublicAccountId = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  try {
    return atob(value);
  } catch {
    return value;
  }
};

const useEmployee = () => {
  const setEmployee = useSetAtom(employeeAtom);
  const setLoggedInEmployee = useSetAtom(loggedInEmployeeAtom);
  const navigate = useNavigate();

  const {data, isLoading, isError, error} = useQuery({
    queryKey: ['profile', 'self'],
    queryFn: getService(EmployeeService).getSelfApiV1PeopleSelfGet,
  });

  useEffect(() => {
    if (isError && error instanceof ApiError && error.status === 404) {
      navigate(ROUTE_PATHS.WAITLIST);
    }
  }, [isError, error, navigate]);

  useEffect(() => {
    if (!data || isLoading) {
      return;
    }

    if (!isCompanyEmail(data.email)) {
      navigate(ROUTE_PATHS.WAITLIST);
      return;
    }

    setEmployee(data);
    setLoggedInEmployee((draft) => {
      draft.employee = {
        ...draft.employee,
        ...data,
      };
    });
  }, [data, isLoading, navigate, setEmployee, setLoggedInEmployee]);
};

const ProfileLoader = ({children}: {children: React.ReactNode}) => {
  const [searchParams] = useSearchParams();
  const {archiveNotification} = useNotificationArchiver();
  const notificationLinkAnalytics = getNotificationLinkAnalytics(searchParams);
  const notificationChannel = notificationLinkAnalytics?.channel;
  const notificationId = notificationLinkAnalytics?.notificationId;
  const notificationType = notificationLinkAnalytics?.notificationType;
  const user = useAtomValue(pulseUserAtom);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const setGrowthbook = useSetAtom(growthbookCacheAtom);
  const setGrowthbookExperiments = useSetAtom(setGrowthbookExperimentsAtom);
  const setGrowthbookReady = useSetAtom(growthbookReadyAtom);
  const isGrowthbookReady = useAtomValue(growthbookReadyAtom);
  useGrowthbookQueryOverrides();
  const {data, status} = useQuery({
    enabled: !!user,
    queryKey: ['profile', user?.email.split('@')[0] ?? ''],
    queryFn: () =>
      getEmployeeService().getProfileApiV1PeopleUserIdGet(user?.email.split('@')[0] ?? ''),
  });

  const setUserProfile = useSetAtom(loggedInEmployeeAtom);

  // Get logged in employee profile
  useEmployee();

  useEffect(() => {
    if (!user) {
      return;
    }
    void queryClient.prefetchQuery({
      queryKey: workstreamDraftPolicyQueryKey,
      queryFn: fetchWorkstreamDraftPolicy,
    });
  }, [queryClient, user]);

  // Prewarm fetch for JWT token for presence/collaboration
  useJWTToken();

  // Enable site-wide presence tracking
  usePresenceTracking();

  // Enable site-wide presence tracking
  useRecordVisit();

  useEffect(() => {
    if (data && status === 'success') {
      setGrowthbookReady(false);
      // Stored values are only a cache. Reset to safe defaults until this
      // session has successfully evaluated the current GrowthBook rules.
      setGrowthbook(defaultGrowthbookFeatureFlags);
      setUserProfile({
        ...data,
        delegatedBy: data.delegatedBy ?? [],
        delegates: data.delegates ?? [],
        workingWithMe: data.workingWithMe ?? '',
        admin: {} as Employee,
      });

      if (data.employee.email) {
        getSprig(data.employee.email);
      }

      analyticsLogger().setProperties({
        type: getUserType(user),
        level: (data.employee.type === 'Regular' ? data.employee.level : data.employee.type) ?? '',
      });

      analyticsLogger().logEvent('WEB_APP_LOADED', {
        nearest_100: Math.round(window.innerWidth / 100) * 100,
        range: getRange(),
        browser_width: window.innerWidth,
        browser_height: window.innerHeight,
        route: normalizedRoute(),
        browser: getBrowserType(),
      });

      const publicUserId = decodePublicAccountId(data.public_account_id);

      growthbook.setAttributes({
        id: data.employee.user_id,
        loggedIn: true,
        dbx51_email_regex: data.employee.email,
        public_user_id: publicUserId,
      });

      growthbook
        .init({streaming: true})
        .then(() => {
          if (!isCompanyEmail(data.employee.email)) {
            navigate(ROUTE_PATHS.WAITLIST);
            return;
          }
          // Evaluate all GrowthBook flags for this user using the same helper the app uses.
          const resolvedFlags = buildGrowthbookFeatureFlagsFromSdk(
            (id, defaultValue) => growthbook.getFeatureValue(id, defaultValue) === true
          );
          const resolvedExperiments = buildGrowthbookExperimentValuesFromSdk((id, defaultValue) =>
            growthbook.getFeatureValue(id, defaultValue)
          );

          // Write the resolved flags into the Jotai atom so the rest of the app picks them up.
          setGrowthbook(resolvedFlags);
          setGrowthbookExperiments(resolvedExperiments);
          setGrowthbookReady(true);

          // Record every resolved flag with Meticulous so it can track which flags were active
          // during this session and optimise session selection across flag combinations.
          // Optional-chaining makes these calls safe no-ops when the recorder is not loaded.
          for (const [name, value] of Object.entries(resolvedFlags)) {
            window.Meticulous?.context.recordFeatureFlag(name, value);
          }
          for (const [name, value] of Object.entries(resolvedExperiments) as [string, string][]) {
            window.Meticulous?.context.recordFeatureFlag(name, value);
          }
        })
        .catch(() => {
          setGrowthbook(defaultGrowthbookFeatureFlags);
          setGrowthbookExperiments(defaultGrowthbookExperimentValues);
          setGrowthbookReady(true);
        });
    }
  }, [
    data,
    navigate,
    setGrowthbook,
    setGrowthbookExperiments,
    setGrowthbookReady,
    setUserProfile,
    status,
    user,
  ]);

  useEffect(() => {
    if (notificationId != null && notificationChannel) {
      archiveNotification({id: notificationId});
      analyticsLogger().logEvent('NOTIFICATION_CLICKED', {
        channel: notificationChannel,
        notification_id: notificationId,
        notification_type: notificationType ?? undefined,
        click: 'link',
      });
      if (notificationChannel === 'email') {
        analyticsLogger().logEvent('NOTIFICATION_EMAIL_CLICKED');
      }
    }
  }, [archiveNotification, notificationChannel, notificationId, notificationType]);

  return isGrowthbookReady ? <>{children}</> : null;
};
